import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Printer, Share2 } from 'lucide-react';
import { saveBlob, type PaperSize, type PosterTheme } from '@/lib/api';
import { Button, Card, ErrorText } from './ui';

/*
 * Poster downloads, shared by quiz, course and invite posters: a light
 * version to print and a dark one to share, each with a preview.
 */

export interface PosterDownload {
  label: string;
  format: 'pdf' | 'png';
  size?: PaperSize;
}

export interface PosterSource {
  /** Identifies the poster in the query cache. */
  key: unknown[];
  file: (theme: PosterTheme, format: 'pdf' | 'png', size?: PaperSize) => Promise<Blob>;
  /** Download name without extension, e.g. `Nirvik-Ready-Poster-abc-light-letter`. */
  fileName: (theme: PosterTheme, d: PosterDownload) => string;
}

/** The print and share cards side by side (stacked on phones). */
export function PosterChoices({ source }: { source: PosterSource }) {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <PosterCard
        source={source}
        theme="light"
        title="For printing"
        hint="Light background that prints well in color or black and white."
        icon={<Printer className="size-4" aria-hidden />}
        downloads={[
          { label: 'US Letter PDF', format: 'pdf', size: 'letter' },
          { label: 'A4 PDF', format: 'pdf', size: 'a4' },
        ]}
      />
      <PosterCard
        source={source}
        theme="dark"
        title="For sharing"
        hint="Dark design for social media, newsletters, email and chat groups."
        icon={<Share2 className="size-4" aria-hidden />}
        downloads={[
          { label: 'PNG image', format: 'png' },
          { label: 'PDF', format: 'pdf', size: 'letter' },
        ]}
      />
    </div>
  );
}

function PosterCard({
  source,
  theme,
  title,
  hint,
  icon,
  downloads,
}: {
  source: PosterSource;
  theme: PosterTheme;
  title: string;
  hint: string;
  icon: React.ReactNode;
  downloads: PosterDownload[];
}) {
  const preview = usePosterPreview(source, theme);
  const download = useMutation({
    mutationFn: async (d: PosterDownload) =>
      saveBlob(
        await source.file(theme, d.format, d.size),
        `${source.fileName(theme, d)}.${d.format}`,
      ),
  });

  return (
    <Card>
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {title}
      </div>
      {/* Two lines' height on both cards, so the previews and buttons line up side by side. */}
      <p className="mt-1 text-sm text-muted-foreground md:min-h-10">{hint}</p>
      <div className="mt-4 aspect-[612/792] overflow-hidden rounded-md border border-border bg-muted">
        {preview.data ? (
          <img
            src={preview.data}
            alt={`${title} poster preview`}
            className="size-full object-contain"
          />
        ) : (
          <p className="p-4 text-sm text-muted-foreground">
            {preview.isError ? 'Preview unavailable.' : 'Preparing preview…'}
          </p>
        )}
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {downloads.map((d) => (
          <Button
            key={d.label}
            variant="secondary"
            disabled={download.isPending}
            onClick={() => download.mutate(d)}
          >
            <Download className="size-4" aria-hidden /> {d.label}
          </Button>
        ))}
      </div>
      <ErrorText error={download.error} />
    </Card>
  );
}

/** The PNG as a data: URL (the CSP allows data: images, not blob:). */
function usePosterPreview(source: PosterSource, theme: PosterTheme) {
  const png = useQuery({
    queryKey: ['poster', ...source.key, theme],
    queryFn: () => source.file(theme, 'png'),
    staleTime: 5 * 60_000,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!png.data) return;
    const reader = new FileReader();
    reader.onload = () => setUrl(reader.result as string);
    reader.readAsDataURL(png.data);
  }, [png.data]);
  return { data: url, isError: png.isError };
}
